/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* 
    Hello there! This is a demonstration of how to override a file from the base template.
    
    It's necessary that the module export interface remain consistent, 
    as other files in the base template rely on constants.js, thus we
    import the underlying constants.js, modifies it and re-export it.
*/

import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS,
    DEFAULT_SHIPMENT_ID
} from '@salesforce/retail-react-app/app/constants'

// original value is 25
DEFAULT_LIMIT_VALUES[0] = 3
DEFAULT_SEARCH_PARAMS.limit = 24
DEFAULT_SEARCH_PARAMS.sort = ''

export const CUSTOM_HOME_TITLE = '🎉 Hello Extensible React Template!'

export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS, DEFAULT_SHIPMENT_ID}

// Custom Variables
export const DEFAULT_SITE_TITLE_YR = 'Yellow Rose by Kendra Scott'
export const DEFAULT_SITE_TITLE_KS = 'Kendra Scott Official | Jewelry, Personalized Gifts & Watches'
export const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1 = 'stoneColor'
export const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2 = 'metal'
export const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3 = 'letter'
export const PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4 = 'carat'

export const YR_HOME_HREF = '/yellow-rose.html'
export const KS_HOME_HREF = '/'
export const CAT_MENU_DEFAULT_NAV_SSR_DEPTH = 1
export const YR_BRAND_ID = 'yellowrose'
export const YR_CATEGORIES_ID = 'yrose-category'
export const GIVES_BACK_CATGEORY_ID = 'our-story-shop-for-good'
export const KENDRAS_PICK_CATEGORY_ID = 'kendras-picks'
export const BASKET_URL = '/cart'
export const GIFT_CERT_EDIT_STORAGE_KEY_PREFIX = 'giftCertEditFormData_'
export const KS_MY_ACCOUNT_URL = '/account'
export const KS_LOGIN_URL = '/login'
export const KS_SIGNUP_URL = '/create-account?action=register'
export const KS_YELLOW_ROSE_STORE_PAGE_URL = '/yellow-rose-stores.html'
export const KS_STORE_PAGE_URL = '/stores/directory'
export const KS_PERSONAL_SHOPPING_SESSION_URL = '/personal-shopping-session.html'
export const KS_PERSONAL_PRIVACY_POLICY_URL = '/privacy.html'
export const KS_PERSONAL_TERMS_CONDITIONS_URL = '/terms-conditions.html'
export const KS_ATTENTIVE_TERMS_LINK = 'http://attn.tv/kendrascott/terms.html'
export const KS_CURBSIDE_PAGE_URL = '/curbside.html#buy-online'
export const KS_WISHLIST_URL = '/wishlist'
export const KS_SEARCH_URL = '/search?q='
export const YR_SEARCH_URL = '/yellow-rose/search?q='
export const KS_CREATE_ACCOUNT_URL = '/create-account'
export const KS_TERMS_URL = '/terms-conditions.html'
export const KS_HOMEPAGE_SCHEMA_MARKUP_ASSET_ID = 'homePageOrganizationSchema'
export const KS_PRIVACY_POLICY_URL = '/privacy.html'
export const PRODUCT_LIST_IMAGE_VIEW_TYPE = 'large'
export const KS_BRAND_ID = 'kendrascott'
export const HIDE_FILTERS = [
    'c_availableForInStorePickup',
    'c_fluidConfigureEnabled',
    'c_suppressFromSearch',
    'c_availableInStores',
    'cgid',
    'C_Gender',
    'c_Gender',
    'In Stock',
    'in_stock_at',
    'online_date',
    'c_collections'
]

export const BM_FILTER_MAPPING = {
    Class_code: 'Category',
    Subclass_code: 'Style',
    Metal_material: 'Metal Material',
    'SKU Color': 'Color',
    variant_gender: 'Gender',
    diamond: 'Diamond Origin'
}

export const BA25_COUPON_CODE = 'BA25'
export const BA50_COUPON_CODE = 'BA50'
export const BIRTHDAY_COUPON_CODES = [BA25_COUPON_CODE, BA50_COUPON_CODE]
export const LOYALTYBIRTHDAYBONUS = 'LOYALTYBIRTHDAYBONUS'
/** Default birthday award period in days when not set in config (mirrors SFRA loyalty-birthday-award-period). */
export const DEFAULT_BIRTHDAY_AWARD_PERIOD_DAYS = 45

// Custom API names
export const DISCOUNTS_INFO_API = 'discounts-info'

// GWP/PWP helpers used across cart/PDP/PLP experiences
export const GWP_PROMOTION_COOKIE_ID = 'gwp_promotion'
export const GWP_THRESHOLD_COOKIE_KEY = 'gwp_threshold'
export const GWP_PROMOTION_ID_REGEX = /gwp/i
export const PWP_PROMOTION_ID_REGEX = /pwp/i

export const BIRTHDAY_MONTHS = [
    {value: 'January', label: 'January', id: 'JAN'},
    {value: 'February', label: 'February', id: 'FEB'},
    {value: 'March', label: 'March', id: 'MAR'},
    {value: 'April', label: 'April', id: 'APR'},
    {value: 'May', label: 'May', id: 'MAY'},
    {value: 'June', label: 'June', id: 'JUN'},
    {value: 'July', label: 'July', id: 'JUL'},
    {value: 'August', label: 'August', id: 'AUG'},
    {value: 'September', label: 'September', id: 'SEP'},
    {value: 'October', label: 'October', id: 'OCT'},
    {value: 'November', label: 'November', id: 'NOV'},
    {value: 'December', label: 'December', id: 'DEC'}
]

// PD Components
export const PD_HOMEPAGE_YR = 'homepage-yr'
export const PD_HOMEPAGE_KS = 'homepage-ks'
export const PD_COLORBAR_PDP = 'colorbar-pdp'
export const PD_HERO = 'pwa.hero'
export const PD_BANNER = 'pwa.banner'
export const PD_CATEGORY_QUICK_LINKS = 'pwa.categoryQuickLinks'
export const PD_IMAGE_LINKS = 'pwa.imagelink'
export const PD_FULL_IMAGE_X4 = 'pwa.fullImageX4'
export const PD_FULL_IMAGE_X4_DESKTOP = 'pwa.fullImageX4Desktop'
export const PD_HEADLINE_SUBTITLE = 'pwa.headlineSubtitle'
export const PD_HERO_SLIDER = 'pwa.heroSlider'
export const PD_IMAGE_ROW_OF_TWO = 'pwa.imageRowOfTwo'
export const PD_SHOP_THE_LOOK = 'pwa.shopTheLook'
export const PD_PLP_STORY_COMPONENT = 'pwa.plpStoryComponent'
export const PD_STORY_TELLING = 'pwa.storyTelling'
export const PD_TEXT_TILES_X4 = 'pwa.textTilesX4'
export const PD_CATEGORY_ROW_TALL = 'pwa.categoryRowTall'
export const PD_FULL_WIDTH_IMAGE_HEADLINE_LINK = 'pwa.fullWidthImageHeadlineLink'
export const PD_PROMO_PAIRABLE_STYLES = 'pwa.promoPairableStyles'
export const PD_SOCIAL_GALLERY = 'pwa.socialGallery'
export const PD_SPACER = 'pwa.spacer'
export const PD_SHOP_THE_LOOK_GALLERY = 'pwa.shopTheLookGallery'
export const PD_SHOP_THE_LOOK_GALLERY_ITEM = 'pwa.shopTheLookGalleryItem'
export const PD_SHOP_THE_LOOK_SLIDER = 'pwa.shopTheLookSlider'
export const PD_SHOP_THE_LOOK_SLIDER_ITEM = 'pwa.shopTheLookSliderItem'
export const PD_FAQ = 'pwa.faq'
export const PD_FAQ_ITEM = 'pwa.faqItem'
export const PD_PDP_MONETATE_CAROUSEL = 'pwa.pdpMonetateCarousel'
export const PD_RECOMMENDATIONS_EINSTEIN = 'pwa.recommendationsEinstein'
export const PD_RECOMMENDATIONS = 'pwa.recommendations'
export const PD_CATEGORY_PRODUCT_CAROUSELS = 'pwa.categoryProductCarousels'
export const PD_SCROLLING_PROMO_BAR = 'pwa.scrollingPromoBar'
export const PD_SCROLLING_PROMO_BAR_ITEM = 'pwa.scrollingPromoBarItem'
export const PD_CONDENSED_CATEGORY_SLIDER = 'pwa.condensedCategorySlider'
export const PD_HERO_PROMOTION_PERCENT_OFF = 'pwa.heroPromotionPercentOff'
export const PD_HERO_PROMOTION_BUY_X_FOR_Y = 'pwa.heroPromotionBuyXForY'
export const PD_HERO_PROMOTION_UP_TO_PERCENT_OFF = 'pwa.heroPromotionUpToPercentOff'
export const PD_HERO_CUSTOM_TEXT_BANNER = 'pwa.heroCustomTextBanner'
export const PD_HERO_XX_VALUE_BADGE = 'pwa.heroXXValueBadge'
export const PD_HERO_TERMS_AND_CONDITIONS = 'pwa.heroTermsAndConditions'
export const PD_HERO_MULTIPLE_CTA = 'pwa.heroMultipleCTAs'
export const PD_PROMO_GRID_TWO_COLUMNS = 'pwa.promoGridTwoColumns'
export const PD_PROMO_GRID_THREE_COLUMNS = 'pwa.promoGridThreeColumns'
export const PD_PROMO_GRID_FOUR_COLUMNS = 'pwa.promoGridFourColumns'
export const PD_PROMO_GRID_ITEM = 'pwa.promoGridItem'
export const PD_TEXT_ONLY = 'pwa.textOnly'
export const PD_EMAIL_SIGNUP = 'pwa.emailSignUp'
export const PD_COUNTDOWN_TIMER_BANNER = 'pwa.countdownTimerBanner'
export const PD_TEXT_ONLY_QUICK_LINKS = 'pwa.textOnlyQuickLinks'
export const PD_TEXT_LINKS = 'pwa.textLink'
export const PD_ESP_PRODUCTS = 'pwa.espProducts'
export const PD_TESTIMONIALS = 'pwa.testimonials'
export const PD_TESTIMONIALS_ITEM = 'pwa.testimonialsItem'
export const PD_PROMO_QUICK_CATEGORY = 'pwa.promoQuickCategory'
export const PD_PROMO_QUICK_CATEGORY_LINK = 'pwa.promoQuickCategoryLink'
export const PD_LINK_GRID = 'pwa.linkGrid'
export const PD_LIST = 'pwa.list'
export const PD_TEXT_MARKUP = 'pwa.textMarkup'

export const CLOUDINARY_CLOUD_NAME = 'kendra-scott'
export const THEME_COLOR = '#FFFFFF'

export const DEFAULT_DNT_STATE = false

export const BR_SEARCH_URL = '/api/bloomreach-caching/'
export const KS_DOMAIN = '.kendrascott.com'
export const PRODUCT_TILE_IMAGE_VIEW_TYPE = 'large'
export const BR_SHOULD_NOT_ENCODED = ['_br_uid_2']
export * from '@salesforce/retail-react-app/app/constants'
export const USEQUERY_OPTIONS = {
    staleTime: 900 * 1000, // 15 minutes
    cacheTime: 900 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    keepPreviousData: true
}
export const SELECTED_ATTRIBUTES_WIDGET_PRODUCTS =
    '(data.(id,brand,currency,imageGroups[?((@.viewType==large)||(@.viewType==swatch))].(images[0:1].(disBaseLink),viewType,variationAttributes.(**)),inventory,master,minOrderQuantity,name,shortDescription,price,pricePerUnit,productPromotions,slugUrl,stepQuantity,type,variants.(**),variationAttributes.(**),variationValues.(**),tieredPrices,inventory, c_bvAverageRating, c_bvRatingRange, c_bvReviewCount, c_classCode, c_color, c_colorGeneral, c_colorMaterial, c_departmentCode, c_isYellowRoseProduct, c_itemStatus, c_masterVariantID, c_material, c_metal, c_metalMaterial, c_program, c_seasonGeneral, c_size, c_styleCode, c_subclassCode, c_promotions, c_images, c_pdpLinks, c_allPdpLinks, c_breadcrumbs, c_primaryCategory, c_globalePrice, c_selectedCurrency, c_selectedCurrencySymbol, c_RingSize, c_hideNameAndPrice, c_itemOrderType, c_giftCard, c_customStatus, c_highValue, c_storeProtectionLevel, c_engagement, c_noDiff, c_hideAddToBag, c_hideBNPL, c_hideFavoriteButton, c_hideNameAndPrice, c_setOrFeatureContentAsset, c_productTitle, c_productDescription, c_hideGwpBmsmMeter, c_isEngravable, c_enableQuickAdd, c_charmType),limit,total)'
export const SELECTED_ATTRIBUTES_PLP_PRODUCTS =
    '(data.(id,brand,currency,imageGroups[?((@.viewType==large)||(@.viewType==swatch))].(images[0:1].(disBaseLink),viewType,variationAttributes.(**)),inventory,master,minOrderQuantity,name,price,pricePerUnit,productPromotions,slugUrl,stepQuantity,type,variants.(**),variationAttributes.(**),variationValues.(**),tieredPrices,inventory, c_bvAverageRating, c_bvRatingRange, c_bvReviewCount, c_classCode, c_color, c_colorGeneral, c_colorMaterial, c_departmentCode, c_isYellowRoseProduct, c_itemStatus, c_masterVariantID, c_material, c_metal, c_metalMaterial, c_program, c_seasonGeneral, c_size, c_styleCode, c_subclassCode, c_promotions, c_images, c_pdpLinks, c_allPdpLinks, c_breadcrumbs, c_primaryCategory, c_globalePrice, c_selectedCurrency, c_selectedCurrencySymbol, c_RingSize, c_hideNameAndPrice, c_itemOrderType, c_giftCard, c_customStatus, c_highValue, c_storeProtectionLevel, c_engagement, c_noDiff, c_availableForInStorePickup, c_availableInStores, c_imageBadges, c_xxValue, c_badge, c_textBadges, c_enableQuickAdd, c_isEngravable, c_charmType),limit,total)'
export const BR_SORT_OPTIONS = [
    // If no sorting options are defined in the environment variable, these fallback options will be used.
    {
        id: 'trending-now',
        value: '',
        label: 'Trending Now'
    },
    {
        id: 'best-matches',
        value: 'bury_product asc,best_seller desc',
        label: 'Best Sellers'
    },
    {
        id: 'top-rated',
        value: 'bury_product asc,rating desc',
        label: 'Top Rated'
    },
    {
        id: 'price-high-to-low',
        value: 'bury_product asc,price desc',
        label: 'Price High To Low'
    },
    {
        id: 'price-low-to-high',
        value: 'bury_product asc,price asc',
        label: 'Price Low To High'
    },
    {
        id: 'new-arrivals',
        value: 'bury_product asc,days_available asc',
        label: 'New Arrivals'
    }
]
export const SCHEMA_PDP = {
    type: 'Product',
    brandName: 'Kendra Scott',
    offerType: 'Offer',
    availability: 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
    organizationType: 'Organization',
    context: 'https://schema.org',
    returnType: 'MerchantReturnPolicy',
    returnURL: 'https://www.kendrascott.com/shipping-returns.html',
    returnCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    additionalType: 'https://schema.org/OnlineReturnPolicy',
    thingType: 'Thing',
    reviewType: 'Review',
    bazaarvoiceIds: ['bv-jsonld-bvloader-summary', 'bv-jsonld-reviews-data']
}

export const SCHEMA_PLP = {
    type: 'ItemList',
    itemListElementType: 'ListItem',
    context: 'https://schema.org'
}
export const ENGAGEMENT_PRODUCTS = ['4142', 'eng']

export const NOINDEX_PAGE_IDs = ['colorbar-pdp']
export const RESIZE_FULL_MOBILE_ONE_THIRD_DESKTOP = [450, 650, 768, 960, 1152, 1290, 1536]
export const RESIZE_FULL_MOBILE_ONE_TWO_DESKTOP = [450, 650, 768, 1152, 1290, 1536, 1920]
export const RESIZE_HALF_MOBILE_ONE_FOURTH_DESKTOP = [225, 325, 384, 576, 650, 768, 960]
export const RESIZE_FULL_MOBILE_ONE_FOURTH_DESKTOP = [450, 650, 768, 840, 960, 1080, 1200]
export const RESIZE_FULL_HERO = [450, 768, 1290, 1536, 1920, 2560, 3840]

export const COLORBAR_BADGE = 'customize'
export const EGIFT_CARD_BALANCE_CHECK_URL = 'https://balance.clutch.com/kendrascott'
export const KS_TERMS_AND_CONFITIONS_URL = '/terms-conditions.html'
export const ENGAGEMENT_BOOK_URL =
    'https://app.acuityscheduling.com/schedule.php?owner=19317596&appointmentType=category:Celebrate%20your%20engagement%20journey%20with%20personalized%201-on-1%20time%20with%20one%20of%20our%20Certified%20Engagement%20Specialists.%20Our%20specialist%20will%20join%20you%20during%20this%20special%20time%20and%20find%20the%20perfect%20Engagement%20Ring%20for%20you%20and%2For%20your%20loved%20one.'
export const ARBITRARY_COUPON_CODE = 'arbitrary-code'
export const SHIPPING = 'SHIPPING'
export const LOYALTY_BDAY_50 = 'LOYALTY_BDAY_50'
export const LOYALTY_BDAY_25 = 'LOYALTY_BDAY_25'
export const BIRTHDAY_50_OFF = 'Birthday 50% Off'
export const BIRTHDAY_25_OFF = 'Birthday 25% Off'
export const BONUS_CHOICE = 'bonus_choice'

export const SHIPPING_COUNTRY_CODES = [{value: 'US', label: 'United States'}]
export const LOQATE_UNKNOWN_KEY_ERROR = '2'
export const LOQATE_GENERIC_ERROR = '1002'
export const TWO_DAY_METHOD_ID = 'Two-Day'
export const GROUND_METHOD_ID = 'Free'
export const CREDIT_CARD_ICON =
    'https://checkoutshopper-live-us.adyen.com/checkoutshopper/images/logos/card.svg'
export const CART_TITLE = 'Your Shopping Bag | Kendra Scott'
export const CHECKOUT_TITLE = 'Checkout | Kendra Scott'
export const CHECKOUT_CONFIRMATION_TITLE = 'Checkout | Kendra Scott Confirmation'
export const DELETE_SHOPPER = '/api/delete-shopper'
